import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Partner } from '../types';
import { partnersApi } from '../services/api/partners';
import { queryKeys } from '../lib/queryKeys';

export interface PartnerDataOptions {
  autoSubscribe?: boolean;
  onPartnerUpdate?: (partner: Partner) => void;
  onPartnerHealthUpdate?: (partnerId: string, healthStatus: string) => void;
}

export interface PartnerDataReturn {
  /** THUB-298: epoch ms of when this query's data last arrived, for a truthful "last updated". */
  dataUpdatedAt?: number;   // optional: test mocks and other callers need not supply it
  partners: Partner[];
  loading: boolean;
  error: string | null;
  connectionStatuses: Map<string, string>;
  getPartner: (id: string) => Partner | undefined;
  getPartnerByName: (name: string) => Partner | undefined;
  refreshPartners: () => void;
  subscribeToPartner: (partnerId: string) => () => void;
  unsubscribeFromPartner: (partnerId: string) => void;
}

export const usePartnerData = (options: PartnerDataOptions = {}): PartnerDataReturn => {
  const queryClient = useQueryClient();

  const { data: partners = [], isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: queryKeys.partners.all,
    queryFn: () => partnersApi.getPartners(),
  });

  const getPartner = useCallback((id: string): Partner | undefined => {
    return partners.find((partner: Partner) => partner.partnerId === id);
  }, [partners]);

  const getPartnerByName = useCallback((name: string): Partner | undefined => {
    return partners.find((partner: Partner) => partner.name === name);
  }, [partners]);

  const refreshPartners = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.partners.all });
  }, [queryClient]);

  const subscribeToPartner = useCallback((_partnerId: string): (() => void) => {
    return () => {};
  }, []);

  const unsubscribeFromPartner = useCallback((_partnerId: string) => {}, []);

  return {
    partners,
    loading: isLoading,
    // THUB-298: when this data actually last arrived, so consumers can show a truthful
    // "last updated" without keeping their own timestamp in step by effect.
    dataUpdatedAt,
    error: error ? (error as Error).message : null,
    connectionStatuses: new Map(),
    getPartner,
    getPartnerByName,
    refreshPartners,
    subscribeToPartner,
    unsubscribeFromPartner,
  };
};
